import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { TrendingUp, TrendingDown, Minus, Sparkles, ChevronDown } from 'lucide-react';
import api from '../../services/api';
import TodaysMission from './TodaysMission';
import BadgesSection from '../BadgesSection';
import SelfieOnboarding from './SelfieOnboarding';
import DistrictAtmosphere from '../district/DistrictAtmosphere';
import { useAvatar } from '../../context/AvatarContext';
import { overallBand } from '../../lib/livingAvatarStates';
import { stateLabelFor, scenerySrc } from '../../lib/worldScenes';
import { PILLAR_WORLD } from '../../lib/worldState';
import { timePhase, healthWeather, PHASE_LABEL, WEATHER_LABEL } from '../../lib/districtAtmosphere';

const STATE_TEXT = { movement: '#D4FF3A', recovery: '#5BDCF5', mental: '#C08CFF', nutrition: '#FFAE5C' };
const LAND_PREFIX = { movement: 'movland', recovery: 'recland', nutrition: 'nutland', mental: 'mndland' };
const SPRITE_PREFIX = { movement: 'movbld', recovery: 'recbld', nutrition: 'nutbld', mental: 'mndbld' };
const PILLAR_BUILDINGS = {
  movement: ['gym', 'studio', 'community'],
  recovery: ['bedroom', 'lab', 'community'],
  nutrition: ['kitchen', 'market', 'community'],
  mental: ['sounds', 'meditation', 'community'],
};
const PLOT_POS = {
  movement:  { gym: { left: '31%', top: '41%' }, studio: { left: '67%', top: '45%' }, community: { left: '47%', top: '69%' } },
  recovery:  { bedroom: { left: '33%', top: '43%' }, lab: { left: '66%', top: '45%' }, community: { left: '48%', top: '66%' } },
  nutrition: { kitchen: { left: '31%', top: '41%' }, market: { left: '67%', top: '45%' }, community: { left: '47%', top: '69%' } },
  mental:    { sounds: { left: '31%', top: '41%' }, meditation: { left: '67%', top: '45%' }, community: { left: '47%', top: '69%' } },
};

// Fixed corners. `anchor` = island centre as % of the stage (for SVG paths + fly origin).
const CORNERS = [
  { pk: 'movement',  cls: 'top-1 left-1',      anchor: [17, 22], origin: '17% 26%', delay: 0.0 },
  { pk: 'recovery',  cls: 'top-1 right-1',     anchor: [83, 22], origin: '83% 26%', delay: 0.6 },
  { pk: 'nutrition', cls: 'bottom-4 left-1',   anchor: [17, 80], origin: '17% 78%', delay: 1.2 },
  { pk: 'mental',    cls: 'bottom-4 right-1',  anchor: [83, 80], origin: '83% 78%', delay: 0.3 },
];

// Painted "above the clouds" hub-world artwork — same art style as the district
// land illustrations. <DistrictAtmosphere/> layers the exact district logic on
// top: time-of-day tints (dawn/dusk/night + stars) and score-driven weather.
const DASH_ART = '/assets/living/dashboard_skyworld.png';

// Weather colour-wash (mood tint over the sky), matching the districts' weather grad.
const WEATHER_GRAD = {
  clear:           'linear-gradient(180deg, rgba(255,224,160,0.10) 0%, rgba(0,0,0,0) 50%, rgba(6,10,24,0.14) 100%)',
  'partly-cloudy': 'linear-gradient(180deg, rgba(200,220,245,0.08) 0%, rgba(0,0,0,0) 52%, rgba(6,10,24,0.16) 100%)',
  crisp:           'linear-gradient(180deg, rgba(190,225,255,0.10) 0%, rgba(0,0,0,0) 50%, rgba(6,10,24,0.14) 100%)',
  overcast:        'linear-gradient(180deg, rgba(120,132,152,0.16) 0%, rgba(30,36,54,0.06) 50%, rgba(6,10,24,0.24) 100%)',
  fog:             'linear-gradient(180deg, rgba(180,190,205,0.18) 0%, rgba(120,130,150,0.06) 50%, rgba(6,10,24,0.22) 100%)',
  'light-rain':    'linear-gradient(180deg, rgba(60,80,120,0.20) 0%, rgba(20,28,48,0.08) 45%, rgba(6,10,24,0.28) 100%)',
};

const MORNING_LINE = {
  dawn:    'Something shifted in your worlds overnight.',
  morning: 'Good morning — your worlds are waking up.',
  midday:  'Your worlds are humming along. Take a look around.',
  dusk:    'Golden hour across your worlds.',
  night:   'Your worlds are winding down for the night.',
};

// Per-world ambient life spec (reused assets only — pure CSS/motion life).
const FIELD = {
  movement:  { anim: 'lh-leaf',    color: '#D4FF3A', n: 6, size: [5, 9], glow: false },
  recovery:  { anim: 'lh-firefly', color: '#5BDCF5', n: 8, size: [3, 5], glow: true },
  nutrition: { anim: 'lh-rise',    color: '#FFAE5C', n: 6, size: [3, 6], glow: false },
  mental:    { anim: 'lh-lantern', color: '#C08CFF', n: 5, size: [6, 10], glow: true },
};

const buildCompositeSrc = (url) => {
  if (!url) return null;
  if (url.startsWith('/api/')) {
    const token = localStorage.getItem('hpme_token');
    return `${process.env.REACT_APP_BACKEND_URL}${url}&auth=${token || ''}`;
  }
  return url;
};

// Ambient particle layer that surrounds an island — replaces the old bouncing.
const AmbientField = ({ pk, reduce }) => {
  const spec = FIELD[pk];
  const bits = useMemo(() => Array.from({ length: spec.n }).map((_, i) => {
    const size = spec.size[0] + Math.random() * (spec.size[1] - spec.size[0]);
    return {
      key: i, size,
      left: `${8 + Math.random() * 84}%`,
      top: `${10 + Math.random() * 80}%`,
      dur: 5 + Math.random() * 5,
      delay: -Math.random() * 6,
      dx: `${(Math.random() - 0.5) * 34}px`,
      dy: `${(Math.random() - 0.5) * 30}px`,
    };
  }), [pk]); // eslint-disable-line react-hooks/exhaustive-deps
  if (reduce) return null;
  return (
    <span aria-hidden className="absolute -inset-3 pointer-events-none overflow-visible">
      {bits.map((b) => (
        <span key={b.key} className="lh-particle absolute rounded-full" style={{
          left: b.left, top: b.top, width: b.size, height: b.size,
          background: spec.color,
          boxShadow: spec.glow ? `0 0 ${b.size * 2.4}px ${spec.color}` : 'none',
          opacity: 0,
          animation: `${spec.anim} ${b.dur}s ease-in-out ${b.delay}s infinite`,
          ['--dx']: b.dx, ['--dy']: b.dy,
        }} />
      ))}
    </span>
  );
};

// A sharp mini-district: top-tier land + its 3 top-tier buildings composited on top.
const MiniDistrict = ({ pk }) => {
  const lp = LAND_PREFIX[pk], sp = SPRITE_PREFIX[pk];
  return (
    <span className="relative block w-[100px] h-[148px]"
      style={{ maskImage: 'radial-gradient(58% 60% at 50% 45%, #000 40%, transparent 82%)', WebkitMaskImage: 'radial-gradient(58% 60% at 50% 45%, #000 40%, transparent 82%)', filter: `drop-shadow(0 14px 14px rgba(0,0,0,0.5)) drop-shadow(0 0 18px ${PILLAR_WORLD[pk].color}44)` }}>
      <img src={`/assets/living/districts/${lp}_city.png`} alt="" draggable={false}
        className="absolute inset-0 w-full h-full object-contain select-none pointer-events-none" />
      {PILLAR_BUILDINGS[pk].map((b) => (
        <img key={b} src={`/assets/living/buildings/${sp}_${b}_t5.png`} alt="" draggable={false}
          className="absolute select-none pointer-events-none"
          style={{ left: PLOT_POS[pk][b].left, top: PLOT_POS[pk][b].top, width: 40, transform: 'translate(-50%, -72%)' }} />
      ))}
    </span>
  );
};

const CornerIsland = ({ pk, cls, delay, score, delta, reduce, flying, onOpen, sceneUrl, restState }) => {
  const meta = PILLAR_WORLD[pk];
  const has = score !== null && score !== undefined;
  const resting = pk === 'movement' && restState === 'planned';
  const TrendIcon = delta == null || delta === 0 ? Minus : delta > 0 ? TrendingUp : TrendingDown;
  const trendColor = delta > 0 ? '#4ade80' : delta < 0 ? '#f87171' : '#a1a1aa';
  const dim = flying && flying !== pk;
  return (
    <motion.button
      type="button"
      onClick={() => onOpen(pk)}
      data-testid={`pillar-island-${pk}`}
      aria-label={`Enter your ${meta.label} world (score ${has ? Math.round(score) : 'not set'})`}
      animate={reduce ? {} : { scale: [1, 1.02, 1], y: [0, -3, 0], opacity: dim ? 0 : 1 }}
      transition={reduce ? {} : { scale: { duration: 6, delay, repeat: Infinity, ease: 'easeInOut' }, y: { duration: 6, delay, repeat: Infinity, ease: 'easeInOut' }, opacity: { duration: 0.4 } }}
      whileTap={{ scale: 0.95 }}
      className={`absolute ${cls} z-30 flex flex-col items-center group`}
    >
      <span className="relative">
        <AmbientField pk={pk} reduce={reduce} />
        {/* the user's own avatar living in THIS pillar's world — whole scene visible, soft-edged */}
        <span className="relative block w-[112px] h-[76px]"
          style={{ filter: `drop-shadow(0 10px 12px rgba(0,0,0,0.5)) drop-shadow(0 0 16px ${meta.color}55)`,
            maskImage: 'radial-gradient(ellipse 60% 68% at 50% 48%, #000 48%, transparent 82%)',
            WebkitMaskImage: 'radial-gradient(ellipse 60% 68% at 50% 48%, #000 48%, transparent 82%)' }}>
          {sceneUrl
            ? <img src={sceneUrl} alt="" draggable={false}
                onError={(e) => { if (!e.currentTarget.dataset.fb) { e.currentTarget.dataset.fb = '1'; e.currentTarget.src = scenerySrc(pk, score); } }}
                className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none" />
            : <span className="absolute inset-0 rounded-2xl bg-white/5" />}
        </span>
        {/* ground contact shadow so the island sits on the land plane */}
        <span aria-hidden className="absolute left-1/2 -translate-x-1/2 bottom-0 w-14 h-3 rounded-[50%]"
          style={{ background: 'radial-gradient(50% 50% at 50% 50%, rgba(0,0,0,0.5) 0%, transparent 75%)' }} />
      </span>
      <span data-testid={`pillar-badge-${pk}`}
        className="-mt-2.5 whitespace-nowrap px-2.5 py-1 rounded-full backdrop-blur-xl bg-black/55 border border-white/15 shadow-lg flex items-center gap-1.5"
        style={{ boxShadow: `0 6px 18px -4px ${meta.color}66` }}>
        <span className="text-[9px] font-bold uppercase tracking-wider text-white/75">{meta.label}</span>
        <span className="font-secondary font-bold text-sm leading-none text-white tabular-nums">{has ? Math.round(score) : (resting ? '🌙' : '—')}</span>
        {has && delta != null && delta !== 0 && <TrendIcon className="w-2.5 h-2.5" style={{ color: trendColor }} />}
        <span className="text-[8px] font-bold uppercase tracking-widest leading-none" style={{ color: STATE_TEXT[pk] }}>
          {has ? stateLabelFor(pk, score) : (resting ? 'Resting' : 'Awaiting')}
        </span>
      </span>
    </motion.button>
  );
};

const LivingHome = () => {
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const { status: avatarStatus, refresh: refreshAvatar, sceneSrc } = useAvatar();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [picking, setPicking] = useState(false);
  const [compositeSrc, setCompositeSrc] = useState(null);
  const [journey, setJourney] = useState(null);
  const [badges, setBadges] = useState(null);
  const [flying, setFlying] = useState(null); // pillar being flown into

  useEffect(() => {
    let off = false;
    const load = async () => {
      try {
        const iq = await api.getWellnessIQ();
        if (off) return;
        setData(iq.data);
      } catch (e) { if (!off) setData(null); }
      finally { if (!off) setLoading(false); }
      api.getJourney().then((r) => { if (!off) setJourney(r.data); }).catch(() => {});
      api.getBadges().then((r) => { if (!off) setBadges(r.data); }).catch(() => {});
      try {
        const res = await api.getAvatarComposite();
        const full = res?.data?.url ? buildCompositeSrc(res.data.url) : null;
        if (!off) setCompositeSrc(full);
      } catch (e) { if (!off) setCompositeSrc(null); }
    };
    load();
    const onRefresh = () => load();
    window.addEventListener('wellness:refresh', onRefresh);
    return () => { off = true; window.removeEventListener('wellness:refresh', onRefresh); };
  }, [avatarStatus]);

  // (cutout removed — dashboard uses the masked composite avatar directly)

  if (avatarStatus !== 'ready' || picking) {
    return (
      <SelfieOnboarding
        onReady={() => { setPicking(false); refreshAvatar(); }}
        onCancel={picking ? () => setPicking(false) : undefined}
      />
    );
  }

  const overall = data?.wellness_iq ?? null;
  const sub = data?.sub_scores || {};
  const deltas = data?.sub_score_deltas || {};
  const band = overallBand(overall);
  const overallColor = overall == null ? '#52525b' : overall >= 80 ? '#CCFF00' : overall >= 60 ? '#facc15' : '#fb923c';
  const phase = timePhase();
  const sky = healthWeather(overall, 'dashboard');

  // Avatar posture reaction — subconscious, no popup. Tall & bright when thriving; slouched & dim when depleted.
  const posture = overall == null || overall < 40
    ? { scale: 0.97, rotate: 1.5, y: 3, filter: 'saturate(0.82) brightness(0.9)' }
    : overall < 60
    ? { scale: 1.0, rotate: 0, y: 0, filter: 'saturate(1) brightness(1)' }
    : { scale: 1.03, rotate: -0.6, y: 0, filter: 'saturate(1.08) brightness(1.08)' };

  const scrollDown = () => window.scrollBy({ top: window.innerHeight * 0.8, behavior: 'smooth' });

  const flyInto = (pk) => {
    if (flying) return;
    if (reduce) { navigate(`/district/${pk}`); return; }
    setFlying(pk);
    setTimeout(() => navigate(`/district/${pk}`), 720);
  };

  const flyOrigin = flying ? (CORNERS.find((c) => c.pk === flying)?.origin || '50% 50%') : '50% 50%';

  return (
    <div data-testid="living-home">
      {/* ===================== CINEMATIC "LIVING WORLD" HERO ===================== */}
      <section className="relative -mx-4 md:-mx-8 h-[80svh] min-h-[600px] overflow-hidden flex flex-col" data-testid="cinematic-hero">
        {/* Painted hub-world base — exactly like the districts' land artwork */}
        <div aria-hidden className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, #6fa8d8 0%, #b9d9ec 55%, #e6f1f7 100%)' }} />
        <img src={DASH_ART} alt="" aria-hidden draggable={false}
          className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none" />
        {/* Gentle grade so overlaid weather + HUD read well over the bright art */}
        <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(8,12,28,0.10) 0%, rgba(0,0,0,0) 30%, rgba(0,0,0,0) 62%, rgba(8,12,28,0.18) 100%)' }} />
        {/* Weather sits ABOVE the framing overlays (like the districts) so clouds/rain/fog are actually visible; still behind the avatar + HUD */}
        <div aria-hidden className="absolute inset-0 z-[7] pointer-events-none">
          <DistrictAtmosphere phase={phase} weather={sky} />
        </div>
        <div aria-hidden className="absolute inset-0 z-[7] pointer-events-none" style={{ background: WEATHER_GRAD[sky] }} />
        <div aria-hidden className="absolute inset-0 z-[5] pointer-events-none" style={{ background: `radial-gradient(52% 40% at 50% 52%, ${overallColor}33 0%, transparent 68%)`, mixBlendMode: 'screen' }} />
        <div aria-hidden className="absolute bottom-0 inset-x-0 z-[5] h-1/4 pointer-events-none" style={{ background: 'linear-gradient(to top, #0b1226 0%, rgba(11,18,38,0.5) 42%, transparent 100%)' }} />
        <div aria-hidden className="absolute inset-0 z-[6] pointer-events-none" style={{ background: 'radial-gradient(78% 62% at 50% 46%, transparent 0%, rgba(4,7,16,0.12) 70%, rgba(4,7,16,0.42) 100%)' }} />

        <div className="relative z-30 flex flex-col h-full px-4 pt-4 pb-4">
          {/* Morning prompt — the reason to log in (fades out during fly-through) */}
          <motion.div animate={{ opacity: flying ? 0 : 1, y: flying ? -8 : 0 }} transition={{ duration: 0.35 }}
            className="shrink-0 flex justify-center" data-testid="morning-prompt">
            <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full backdrop-blur-md bg-black/35 border border-white/12 text-[11px] text-white/85 max-w-full">
              <Sparkles className="w-3.5 h-3.5 shrink-0" style={{ color: overallColor }} />
              <span className="truncate">{MORNING_LINE[phase]}</span>
            </span>
          </motion.div>

          {/* Reframed hero — WORLD first, score supporting */}
          <motion.div animate={{ opacity: flying ? 0 : 1 }} transition={{ duration: 0.35 }}
            className="text-center shrink-0 mt-2" data-testid="wellness-iq-hero">
            <p className="text-[11px] uppercase tracking-[0.35em] font-bold text-white/70 drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)]">Your world is</p>
            <div className="flex items-center justify-center gap-3 mt-0.5">
              <span className="font-secondary font-black text-3xl sm:text-4xl tracking-tight leading-none" data-testid="living-state-label"
                style={{ color: band.color, textShadow: `0 0 30px ${band.color}88, 0 3px 12px rgba(0,0,0,0.85)` }}>
                {band.label}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-black/40 backdrop-blur-md px-2.5 py-1" data-testid="living-iq-pill">
                <span className="text-[8px] uppercase tracking-widest font-bold text-white/55">IQ</span>
                <span className="font-secondary font-bold text-sm tabular-nums" data-testid="living-overall-score" style={{ color: overallColor }}>
                  {overall == null ? '—' : Math.round(overall)}
                </span>
              </span>
            </div>
            <p className="mt-1.5 text-[10px] uppercase tracking-[0.28em] font-semibold text-white/65 drop-shadow-[0_1px_6px_rgba(0,0,0,0.9)]" data-testid="dashboard-atmosphere-label">
              {PHASE_LABEL[phase]} · {WEATHER_LABEL[sky]}
            </p>
          </motion.div>

          {/* stage: connected archipelago (avatar centre + 4 worlds joined by energy paths) */}
          <motion.div
            className="relative flex-1 min-h-0 max-w-[460px] w-full mx-auto"
            data-testid="avatar-arena"
            style={{ transformOrigin: flyOrigin }}
            animate={flying ? { scale: 3.2 } : { scale: 1 }}
            transition={{ duration: 0.72, ease: [0.32, 0.72, 0, 1] }}
          >
            {/* energy paths from centre → each world */}
            <svg aria-hidden className="absolute inset-0 w-full h-full pointer-events-none z-10" viewBox="0 0 100 100" preserveAspectRatio="none">
              {CORNERS.map((c) => (
                <path key={c.pk}
                  d={`M50 50 Q ${(50 + c.anchor[0]) / 2} ${(50 + c.anchor[1]) / 2 + (c.anchor[1] < 50 ? -6 : 6)} ${c.anchor[0]} ${c.anchor[1]}`}
                  fill="none" stroke={PILLAR_WORLD[c.pk].color} strokeWidth="1.6" strokeLinecap="round"
                  strokeDasharray="3 4" vectorEffect="non-scaling-stroke"
                  className={reduce ? '' : 'lh-flow'} style={{ opacity: 0.75, filter: `drop-shadow(0 0 4px ${PILLAR_WORLD[c.pk].color})` }} />
              ))}
            </svg>

            {/* centre avatar — masked composite, perfectly centred & blending in */}
            <div className="absolute inset-0 z-20 pointer-events-none flex items-center justify-center">
              <div className="relative flex flex-col items-center">
                {/* soft score-coloured glow behind the figure */}
                <div aria-hidden className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full"
                  style={{ background: `radial-gradient(circle, ${overallColor}2b 0%, transparent 64%)`, filter: 'blur(12px)' }} />
                <motion.div
                  className="relative w-[260px] sm:w-[300px] h-[300px] sm:h-[348px]"
                  animate={reduce ? {} : { y: [0, -6, 0] }}
                  transition={reduce ? {} : { duration: 4.6, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <div className="absolute inset-0" style={{ transform: `scale(${posture.scale}) rotate(${posture.rotate}deg)`, transformOrigin: '50% 50%' }}>
                    {compositeSrc && (
                      <img src={compositeSrc} alt="Your wellness avatar" draggable={false}
                        data-testid="avatar-center-orb"
                        className="absolute inset-0 w-full h-full object-cover select-none"
                        style={{ objectPosition: 'center 20%',
                          maskImage: 'radial-gradient(ellipse 47% 58% at 50% 42%, #000 55%, transparent 82%)',
                          WebkitMaskImage: 'radial-gradient(ellipse 47% 58% at 50% 42%, #000 55%, transparent 82%)',
                          filter: `${posture.filter} drop-shadow(0 16px 22px rgba(0,0,0,0.45))` }} />
                    )}
                  </div>
                  {/* soft contact glow at the feet */}
                  <div aria-hidden className="absolute left-1/2 bottom-2 -translate-x-1/2 w-[200px] h-[46px] rounded-[100%]"
                    style={{ background: `radial-gradient(closest-side, ${overallColor}44, transparent)`, mixBlendMode: 'screen', filter: 'blur(14px)' }} />
                </motion.div>
              </div>
            </div>

            {CORNERS.map((c) => (
              <CornerIsland key={c.pk} pk={c.pk} cls={c.cls} delay={c.delay}
                score={sub[c.pk]} delta={deltas[c.pk]} reduce={reduce} flying={flying}
                restState={data?.rest_state}
                sceneUrl={sceneSrc ? sceneSrc(c.pk, sub[c.pk]) : null}
                onOpen={flyInto} />
            ))}
          </motion.div>

          <motion.div animate={{ opacity: flying ? 0 : 1 }} transition={{ duration: 0.3 }}
            className="shrink-0 flex flex-col items-center gap-2 mt-1">
            <p className="text-[11px] text-white/70 flex items-center gap-1.5 drop-shadow" data-testid="arena-hint">
              <Sparkles className="w-3 h-3" style={{ color: overallColor }} /> Tap a world to step inside
            </p>
            <button onClick={() => setPicking(true)} data-testid="change-avatar-btn"
              className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-black/30 backdrop-blur-md px-4 py-1.5 text-xs font-medium text-zinc-100 hover:text-white hover:bg-white/10 hover:border-white/40 transition-colors">
              <Sparkles className="w-3.5 h-3.5" /> Update AI Avatar
            </button>
            <button onClick={scrollDown} data-testid="hero-scroll-cue" aria-label="Scroll for more"
              className="mt-0.5 text-white/55 hover:text-white/90 transition-colors">
              <ChevronDown className="w-6 h-6 animate-bounce" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* ===================== BELOW THE FOLD (unchanged content) ===================== */}
      <div className="max-w-md mx-auto pb-4 pt-4 space-y-3">
        {journey?.today_breakdown && <TodaysMission breakdown={journey.today_breakdown} />}
        {badges?.badges && (
          <BadgesSection badges={badges.badges} earnedCount={badges.earned_count} totalCount={badges.total_count} compact />
        )}
        {overall == null && !loading && (
          <p className="text-xs text-zinc-500 mt-4 text-center" data-testid="living-home-empty">
            Log a check-in, take the Mobility baseline, or chat with Nikki — your avatar and worlds come alive as you use the app.
          </p>
        )}
      </div>
    </div>
  );
};

export default LivingHome;
